import React, { useState, useEffect } from 'react';
import { supabase } from '../../lib/db.ts';
import { Loader2 } from 'lucide-react';

interface TermsContent {
  id: string;
  title: string;
  content: string;
  version: number;
  updated_at: string;
}

export function TermsAndConditionsManager() {
  const [content, setContent] = useState<TermsContent | null>(null);
  const [title, setTitle] = useState('');
  const [termsContent, setTermsContent] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  useEffect(() => {
    fetchCurrentContent();
  }, []);

  const fetchCurrentContent = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .rpc('get_active_site_content', { p_content_type: 'terms' });

      if (error) throw error;

      if (data && data.length > 0) {
        const currentContent = data[0];
        setContent(currentContent);
        setTitle(currentContent.title);
        setTermsContent(currentContent.content);
      }
    } catch (err) {
      setError('Failed to load current terms and conditions');
      console.error('Error fetching content:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setIsSaving(true);
      setError(null);
      setSuccess(null);

      if (!title.trim()) {
        setError('Title is required');
        return;
      }

      if (!termsContent.trim()) {
        setError('Content is required');
        return;
      }

      const { error } = await supabase
        .rpc('update_site_content', {
          p_content_type: 'terms',
          p_title: title,
          p_content: termsContent
        });

      if (error) throw error;

      setSuccess('Terms and conditions updated successfully');
      await fetchCurrentContent();
    } catch (err) {
      setError('Failed to update terms and conditions');
      console.error('Error updating content:', err);
    } finally {
      setIsSaving(false);
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="bg-white p-8 rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6">Terms and Conditions</h2>
      
      {error && (
        <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-md text-red-600">
          {error}
        </div>
      )}
      
      {success && (
        <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-md text-green-600">
          {success}
        </div>
      )}

      <div className="space-y-6">
        <div>
          <label htmlFor="title" className="block text-sm font-medium text-gray-700 mb-1">
            Title
          </label>
          <input
            type="text"
            id="title"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            placeholder="Enter title"
          />
        </div>

        <div>
          <label htmlFor="content" className="block text-sm font-medium text-gray-700 mb-1">
            Content
          </label>
          <textarea
            id="content"
            value={termsContent}
            onChange={(e) => setTermsContent(e.target.value)}
            rows={20}
            className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 font-mono"
            placeholder="Enter terms and conditions content"
          />
        </div>

        {content && (
          <div className="text-sm text-gray-500">
            <p>Current version: {content.version}</p>
            <p>Last updated: {new Date(content.updated_at).toLocaleString()}</p>
          </div>
        )}

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isSaving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin inline mr-2" />
                Saving...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </div>
  );
} 